import {
    faCheck,
    faSpinner,
    faTimes,
    faTrash,
    IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {boundMethod} from "autobind-decorator";
import {debounce} from "lodash";
import React from "react";
import {IntlContext, IntlShape} from "react-intl";

import {ILocalizedText} from "@translate/models";
import {BUTTON_ANIMATION_DELAY} from "../models";
import {EButtonState} from "./models";

import T from "@translate/T";
import Tooltip from "../building-blocks/Tooltip";

interface IDeleteButtonProps {
    onDelete(): Promise<boolean>;

    classNameButton?: string;
    disabled?: boolean;
    id?: string;
    noAnimation?: boolean;
    noMargin?: boolean;
    otherIcon?: IconDefinition;
    otherName?: JSX.Element;
    skipConfirm?: boolean;
    title?: ILocalizedText;
    titleDeleteWarning?: ILocalizedText;
}

interface IDeleteButtonState {
    showConfirm: boolean;
    deleteState: EButtonState;
}

class DeleteButton extends React.PureComponent<
    IDeleteButtonProps,
    IDeleteButtonState
> {
    public readonly state: IDeleteButtonState = {
        showConfirm: false,
        deleteState: EButtonState.None,
    };

    public extraText: JSX.Element | null = null;

    private readonly reset = debounce(
        () =>
            this.setState(
                {deleteState: EButtonState.None},
                () => (this.extraText = null),
            ),
        BUTTON_ANIMATION_DELAY,
    );

    public componentWillUnmount() {
        this.reset.cancel();
    }

    @boundMethod
    public async delete(e: React.SyntheticEvent) {
        e.preventDefault();

        const {noAnimation, onDelete} = this.props;
        this.reset.cancel();
        this.setState({
            showConfirm: false,
            deleteState: EButtonState.InProgress,
        });

        const deleted = await onDelete();
        if (deleted && noAnimation) {
            return;
        }

        this.setState(
            {deleteState: deleted ? EButtonState.Done : EButtonState.Error},
            this.reset,
        );
    }

    @boundMethod
    public toogleDeleteButton(e: React.SyntheticEvent) {
        if (this.props.skipConfirm) {
            this.delete(e);
            return;
        }

        e.preventDefault();

        this.setState((prevState) => ({showConfirm: !prevState.showConfirm}));
    }

    public render() {
        const {showConfirm} = this.state;

        if (!showConfirm) {
            return <IntlContext.Consumer children={this.renderDeleteButton} />;
        }

        return <IntlContext.Consumer children={this.renderButtonGroup} />;
    }

    @boundMethod
    private renderDeleteButton(intl: IntlShape) {
        const {classNameButton, id, disabled, title, otherName, noMargin} =
            this.props;
        const {deleteState} = this.state;

        const btnProps = this.getClassName(deleteState);
        const addClassName = classNameButton ? " " + classNameButton : "";

        if (noMargin) {
            btnProps.className = btnProps.className.replace(" mr-1", "");
        }

        return (
            <Tooltip
                content={this.getTooltip()}
                visible={true}
                noSpanElement={true}
            >
                <button
                    type="button"
                    id={id}
                    className={btnProps.className + addClassName}
                    disabled={
                        disabled || deleteState === EButtonState.InProgress
                    }
                    title={title?.(intl)}
                    onClick={this.toogleDeleteButton}
                >
                    <FontAwesomeIcon
                        icon={btnProps.icon}
                        fixedWidth={true}
                        className="mr-1"
                        spin={deleteState === EButtonState.InProgress}
                    />
                    {otherName ?? <T>Delete</T>}
                </button>
            </Tooltip>
        );
    }

    @boundMethod
    private renderButtonGroup(intl: IntlShape) {
        const {id, titleDeleteWarning} = this.props;

        return (
            <div className="btn-group">
                <Tooltip
                    content={titleDeleteWarning?.(intl)}
                    noSpanElement={true}
                >
                    <button
                        type="button"
                        id={id ? `confirm-${id}` : "confirm"}
                        className="btn btn-danger"
                        onClick={this.delete}
                    >
                        <FontAwesomeIcon
                            icon={faTrash}
                            fixedWidth={true}
                            className="mr-1"
                        />
                        <T>Confirm</T>
                    </button>
                </Tooltip>
                <button
                    type="button"
                    id={id ? `cancel-${id}` : "cancel"}
                    className="btn btn-secondary"
                    onClick={this.toogleDeleteButton}
                >
                    <FontAwesomeIcon
                        icon={faTimes}
                        fixedWidth={true}
                        className="mr-1"
                    />
                    <T>Cancel</T>
                </button>
            </div>
        );
    }

    private getClassName(saveState: EButtonState) {
        const {otherIcon} = this.props;

        switch (saveState) {
            case EButtonState.Done:
                return {className: "btn btn-dark mr-1", icon: faCheck};

            case EButtonState.Error:
                return {className: "btn btn-secondary mr-1", icon: faTimes};

            case EButtonState.InProgress:
                return {className: "btn btn-danger mr-1", icon: faSpinner};

            default:
                return {
                    className: "btn btn-danger mr-1",
                    icon: otherIcon ?? faTrash,
                };
        }
    }

    private getTooltip() {
        const {deleteState} = this.state;
        if (deleteState !== EButtonState.Error) {
            return null;
        }

        return (
            <span id="not-removed" className="text-warning">
                <T>Could not be removed.</T>
                {this.extraText}
            </span>
        );
    }
}

export default DeleteButton;
